import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons/faCaretRight";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import DocumentElement from "../DocumentView/DocumentElement";
import PageRichText from "./PageRichText";
import { usePageTextBlock } from "./usePageTextBlock";
import { useIsPrint } from "../DocumentView/DocumentContext";
import { cn } from "../lib/utils";

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

interface Props {
  element: DocumentElementType;
}

/**
 * A toggle (collapsible section): a caret + title row that shows/hides its
 * child blocks. The title is a page text surface stored in `data.content` —
 * a collab text-mirror key, so the title gets its own Y fragment plus the full
 * slash-menu / arrow-navigation / Enter / Backspace wiring in the editor host.
 * This component is the read/fill/print renderer — the editor host renders its
 * own editable header + nested drop zone (DocumentEditorView's
 * PageContainerBox). The authored `collapsed` flag only sets the INITIAL state
 * here; readers can open/close freely (local state).
 */
const PageToggle: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    content?: string;
    title?: string;
    collapsed?: boolean;
  };
  const [open, setOpen] = useState(data?.collapsed !== true);
  // In a static print/PDF render there's no way to expand a collapsed toggle,
  // so always show its content — otherwise the blocks are silently lost.
  const isPrint = useIsPrint();
  const showChildren = open || isPrint;
  const children = element.elements || [];
  const { richTextProps } = usePageTextBlock(element);
  // Legacy toggles (pre rich-title) stored a plain string in `data.title`.
  const legacy =
    !data?.content && typeof data?.title === "string" && data.title
      ? `<p>${escapeHtml(data.title)}</p>`
      : null;

  return (
    <div>
      <div className="flex w-full items-start gap-[8px] px-[4px] py-[3px]">
        <button
          type="button"
          aria-label={open ? "Collapse" : "Expand"}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "mt-[2px] flex h-[20px] w-[20px] flex-none items-center justify-center rounded-[5px] text-[#8f8b83] transition-transform hover:bg-[#f0efec]",
            open && "rotate-90",
          )}
        >
          <FontAwesomeIcon icon={faCaretRight} className="text-[13px]" />
        </button>
        <div className="min-w-0 flex-1">
          <PageRichText
            {...richTextProps}
            value={legacy ?? richTextProps.value}
            plain
            singleLine
            className="font-jakarta text-[16px] font-semibold leading-[1.5] text-[#2a2723] [&_p]:my-0"
          />
        </div>
      </div>
      {showChildren && (
        <div className="ml-[10px] border-l border-[#eceae5] pl-[18px] pt-[2px]">
          {children.map((child) => (
            <DocumentElement key={child.id} element={child} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PageToggle;
